import { ComponentType } from 'react';

import SortAscIcon from '@/assets/sort-asc.svg?react';

import { styled } from '@mui/joy';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import { DataGridPro, gridClasses } from '@mui/x-data-grid-pro';
import { DataGridProProps } from '@mui/x-data-grid-pro';

import { ROW_CLASS_ERROR_STATE } from '@components/Table';

const getStyledGrid = <T extends ComponentType<DataGridProProps>>(
  Component: T,
) =>
  styled(Component)<{
    noLineStyles?: boolean;
    autoRowHeight?: boolean;
    footer?: boolean;
  }>(({ theme, noLineStyles, autoRowHeight, footer }) => ({
    border: 2,
    ':root, &': {
      '--DataGrid-containerBackground': 'transparent',
    },
    '.MuiCollapse-root': {
      backgroundColor: 'transparent !important',
    },
    '&.MuiDataGrid-root--noToolbar .MuiDataGrid-columnHeader': {
      background: theme.palette.isDarkMode
        ? theme.palette.neutral[200]
        : theme.palette.neutral[50],
      '&:nth-of-type(2)': {
        borderTopLeftRadius: theme.palette.isDarkMode ? 0 : theme.spacing(1),
      },
      '&.MuiDataGrid-columnHeader--last': {
        borderTopRightRadius: theme.palette.isDarkMode ? 0 : theme.spacing(1),
      },
    },
    '.MuiDataGrid-columnHeader:focus': {
      outline: 0,
    },
    '.MuiDataGrid-columnHeaderTitleContainer': {
      display: 'flex',
      gap: theme.spacing(1),
    },
    '.MuiDataGrid-columnHeaderTitleContainerContent ': {
      fontSize: 16,
      color: theme.palette.isDarkMode
        ? theme.palette.neutral[600]
        : theme.palette.neutral[700],
    },
    '.MuiDataGrid-cell': {
      border: 0,
      borderTop: '1.5px solid transparent',
      borderBottom: '1.5px solid transparent',
      '&:nth-of-type(2)': {
        borderLeft: '1.5px solid transparent',
        borderRadius: `${theme.spacing(1)} 0 0 ${theme.spacing(1)}`,
      },
      '&:last-of-type': {
        borderRadius: `0 ${theme.spacing(1)} ${theme.spacing(1)} 0`,
      },
      '&[data-field]:has(+ .MuiDataGrid-cellEmpty)': {
        borderRight: '1.5px solid transparent',
      },
      '&:focus': {
        outline: 0,
      },
      '&.MuiDataGrid-cellSkeleton': {
        '&:first-of-type': {
          borderRadius: `${theme.spacing(1)} 0 0 ${theme.spacing(1)}`,
        },
        '&:nth-of-type(2)': {
          borderRadius: 0,
        },
      },
    },
    '.MuiDataGrid-columnSeparator': {
      display: 'none',
    },
    '.MuiDataGrid-row': {
      color: theme.palette.isDarkMode
        ? theme.palette.neutral[600]
        : theme.palette.neutral[700],
    },
    '.MuiDataGrid-row.row-even .MuiDataGrid-cell': !noLineStyles && {
      background: theme.palette.isDarkMode
        ? theme.palette.neutral[300]
        : theme.palette.neutral[100],
    },
    '.MuiDataGrid-row.row-odd .MuiDataGrid-cell': !noLineStyles && {
      background: 'transparent', // or theme.palette.neutral[50]/white
    },
    '.MuiDataGrid-row:not(.MuiDataGrid-rowSkeleton):hover': {
      background: 'none',
      '.MuiDataGrid-cell': {
        background: noLineStyles ? 'none' : theme.palette.neutral[200],
      },
    },
    '.MuiDataGrid-row.row-selected .MuiDataGrid-cell': {
      borderTop: `1.5px solid ${theme.palette.primary[500]}`,
      borderBottom: `1.5px solid ${theme.palette.primary[500]}`,
      transition: 'border-color 2s ease-out',
      '&:nth-of-type(2)': {
        borderLeft: `1.5px solid ${theme.palette.primary[500]}`,
        borderRadius: `${theme.spacing(1)} 0 0 ${theme.spacing(1)}`,
      },
      '&[data-field]:has(+ .MuiDataGrid-cellEmpty)': {
        borderRight: `1.5px solid ${theme.palette.primary[500]}`,
        borderRadius: `0 ${theme.spacing(1)} ${theme.spacing(1)} 0`,
      },
    },
    '.MuiDataGrid-row.row-selected-fading .MuiDataGrid-cell': {
      borderTopColor: 'transparent',
      borderBottomColor: 'transparent',
      '&:nth-of-type(2)': {
        borderLeftColor: 'transparent',
      },
      '&[data-field]:has(+ .MuiDataGrid-cellEmpty)': {
        borderRightColor: 'transparent',
      },
    },
    '.MuiDataGrid-filler div': {
      borderTop: 'none',
    },
    '.MuiDataGrid-footerContainer': {
      borderRadius: theme.spacing(1),
      background: theme.palette.neutral[200],
      marginBottom: 0,
      minHeight: 'unset',
      border: 0,
    },
    '.MuiDataGrid-row .MuiDataGrid-cell': autoRowHeight && {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
    '.MuiDataGrid-scrollbarFiller': {
      border: 'none !important',
    },
    '.MuiDataGrid-main ': {
      flexGrow: footer ? 0 : 1,
    },
    [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
      outline: 'none',
    },
    '&.MuiDataGrid-root .MuiDataGrid-columnHeaders': {
      boxShadow: '{CSS box-shadow}',
    },
    [`& .MuiDataGrid-row.${ROW_CLASS_ERROR_STATE}`]: {
      borderColor: theme.palette.danger[300],
      borderWidth: '1px',
      borderStyle: 'solid',
      borderRadius: theme.spacing(1),
      overflow: 'hidden',
    },

    '.MuiDataGrid-overlayWrapper': {
      minHeight: '150px',
    },
  }));

export const StyledDataGridPro = getStyledGrid(DataGridPro);

export const SvgPlaceholder = styled(SortAscIcon)({
  path: {
    fill: '#9499AA',
  },
});

export const GroupedCellStack = styled(Stack)({
  flex: 1,
  marginLeft: '-8px',
  marginRight: '-8px',
  '.MuiDataGrid-main': {
    flexGrow: 0,
  },
  '.MuiDataGrid-columnHeader': {
    borderBottom: 'none !important',
  },
  '.MuiDataGrid-row .MuiDataGrid-cell:nth-of-type(2)': {
    paddingLeft: '30px',
  },
  '& > .MuiStack-root': {
    height: 'auto',
  },
});

export const GroupedCellCaption = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(1),
  background: theme.palette.isDarkMode
    ? theme.palette.neutral[300]
    : theme.palette.neutral[100],
  borderRadius: theme.spacing(1),
  cursor: 'pointer',
  color: theme.palette.neutral[700],
  fontWeight: 400,
  fontSize: theme.fontSize.sm,
}));
